/**
 * UTF-8 aware Base64 transforms for the Base64 tool.
 *
 * The browser's `btoa`/`atob` only operate on latin-1 code units, so feeding
 * them text directly either produced the wrong bytes (`"héllo"` -> latin-1
 * 0xE9) or threw outright (emoji, Cyrillic, CJK). Encode the text to UTF-8
 * bytes first and decode back the same way, which matches `base64` on the
 * command line and every other Base64 tool.
 *
 * ASCII input is unaffected: its UTF-8 bytes are the ASCII bytes, so the
 * output is byte-for-byte what `btoa`/`atob` already produced.
 */

/** Pack the UTF-8 bytes into a binary string that `btoa` accepts. */
const bytesToBinaryString = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return binary;
};

/** UTF-8 encode the text, then Base64 the resulting bytes. */
export const encodeBase64 = (input: string): string =>
  btoa(bytesToBinaryString(new TextEncoder().encode(input)));

/**
 * Base64-decode to bytes, then UTF-8 decode the bytes back to text.
 *
 * Invalid UTF-8 byte sequences become U+FFFD replacement characters rather
 * than throwing, matching `Buffer.from(value, 'base64').toString('utf8')`.
 */
export const decodeBase64 = (input: string): string => {
  const binary = atob(input);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder('utf-8').decode(bytes);
};
